import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, Wrench, MessageSquare, ShieldCheck, ArrowRight, Clock, CheckCircle2, AlertCircle, User } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';

const StatusPill = ({ status }) => {
  const styles = {
    'Submitted': 'bg-[#E8DDFF] text-[#1F0068]',
    'Assigned': 'bg-[#FFEDD6] text-[#1F0068]',
    'Engineer on the way': 'bg-[#FCE0EC] text-[#1F0068]',
    'In progress': 'bg-[#FF5C39] text-white',
    'Resolved': 'bg-[#00b67a] text-white',
  };
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${styles[status] || 'bg-[#FFF6EB] text-[#1F0068]'}`}>{status}</span>;
};

const Dashboard = () => {
  const { user } = useAuth();
  const { claims } = useData();

  const active = claims.filter((c) => c.status !== 'Resolved');
  const resolved = claims.filter((c) => c.status === 'Resolved');

  return (
    <div className="bg-dhs min-h-screen">
      <Header />
      <main className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-5">
          {/* Welcome card */}
          <div className="bg-[#1F0068] text-white rounded-3xl p-6 md:p-10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full blob-1 opacity-20" />
            <div className="relative grid md:grid-cols-3 gap-6 items-start">
              <div className="md:col-span-2">
                <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-xs font-semibold mb-3">
                  <User className="w-3.5 h-3.5" /> Welcome back
                </div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Hi, {user?.name}</h1>
                <p className="mt-2 text-white/80">Here’s a snapshot of your plans, repairs and claims.</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-5 backdrop-blur-sm">
                <div className="text-xs text-white/70">Policy number</div>
                <div className="font-bold text-lg">{user?.policyNumber}</div>
                <div className="mt-2 text-xs text-white/70">Active plan</div>
                <div className="font-bold text-sm text-[#FF5C39]">{user?.plan}</div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="grid md:grid-cols-4 gap-4 mt-6">
            <Link to="/dashboard/new" className="dhs-card bg-white rounded-2xl p-6 border border-[#1F0068]/5 flex items-center gap-4 group">
              <div className="w-14 h-14 rounded-2xl bg-[#FFEDD6] flex items-center justify-center text-[#1F0068]"><Wrench className="w-6 h-6" /></div>
              <div className="flex-1">
                <div className="font-bold text-[#1F0068]">Book a repair</div>
                <div className="text-xs text-[#1F0068]/60">Get an engineer to your door</div>
              </div>
              <ArrowRight className="w-5 h-5 text-[#FF5C39] group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/triage" className="dhs-card bg-white rounded-2xl p-6 border border-[#1F0068]/5 flex items-center gap-4 group">
              <div className="w-14 h-14 rounded-2xl bg-[#E8DDFF] flex items-center justify-center text-[#1F0068]"><AlertCircle className="w-6 h-6" /></div>
              <div className="flex-1">
                <div className="font-bold text-[#1F0068]">Ask Homey AI</div>
                <div className="text-xs text-[#1F0068]/60">Quick symptom triage</div>
              </div>
              <ArrowRight className="w-5 h-5 text-[#FF5C39] group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/dashboard/new?type=complaint" className="dhs-card bg-white rounded-2xl p-6 border border-[#1F0068]/5 flex items-center gap-4 group">
              <div className="w-14 h-14 rounded-2xl bg-[#FCE0EC] flex items-center justify-center text-[#1F0068]"><MessageSquare className="w-6 h-6" /></div>
              <div className="flex-1">
                <div className="font-bold text-[#1F0068]">Make a complaint</div>
                <div className="text-xs text-[#1F0068]/60">Tell us what went wrong</div>
              </div>
              <ArrowRight className="w-5 h-5 text-[#FF5C39] group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/quote" className="dhs-card bg-white rounded-2xl p-6 border border-[#1F0068]/5 flex items-center gap-4 group">
              <div className="w-14 h-14 rounded-2xl bg-[#FFE0D6] flex items-center justify-center text-[#1F0068]"><ShieldCheck className="w-6 h-6" /></div>
              <div className="flex-1">
                <div className="font-bold text-[#1F0068]">Add a plan</div>
                <div className="text-xs text-[#1F0068]/60">Cover another appliance</div>
              </div>
              <ArrowRight className="w-5 h-5 text-[#FF5C39] group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Active claims */}
          <div className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-[#1F0068]">Active claims & repairs</h2>
              <Link to="/dashboard/new"><Button className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white gap-1.5 font-semibold"><Plus className="w-4 h-4" /> New</Button></Link>
            </div>

            {active.length === 0 && (
              <div className="bg-white rounded-3xl border border-[#1F0068]/5 p-10 text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#FFEDD6] flex items-center justify-center text-[#1F0068] mx-auto mb-4"><CheckCircle2 className="w-6 h-6" /></div>
                <p className="text-[#1F0068] font-bold">No open claims right now</p>
                <p className="text-sm text-[#1F0068]/60 mt-1">You’re all sorted. Book a repair or add a plan any time.</p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {active.map((c) => (
                <Link key={c.id} to={`/dashboard/claim/${c.id}`} className="dhs-card block bg-white rounded-2xl p-6 border border-[#1F0068]/5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs text-[#1F0068]/60 font-semibold">{c.type} · {c.id}</div>
                      <div className="font-bold text-[#1F0068] mt-0.5">{c.appliance}</div>
                    </div>
                    <StatusPill status={c.status} />
                  </div>
                  <p className="mt-3 text-sm text-[#1F0068]/70 line-clamp-2">{c.description}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-[#1F0068]/60">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Opened {new Date(c.createdAt).toLocaleDateString('en-GB')}</span>
                    <span className="font-semibold text-[#FF5C39]">View details →</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Resolved */}
          {resolved.length > 0 && (
            <div className="mt-10">
              <h2 className="text-2xl font-bold text-[#1F0068] mb-4">History</h2>
              <div className="bg-white rounded-3xl border border-[#1F0068]/5 divide-y divide-[#1F0068]/5">
                {resolved.map((c) => (
                  <Link key={c.id} to={`/dashboard/claim/${c.id}`} className="flex items-center gap-4 p-5 hover:bg-[#FFF6EB] transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-[#00b67a]/15 text-[#00b67a] flex items-center justify-center"><CheckCircle2 className="w-5 h-5" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[#1F0068] truncate">{c.appliance} · {c.type}</div>
                      <div className="text-xs text-[#1F0068]/60">Resolved on {new Date(c.updates[c.updates.length - 1].at).toLocaleDateString('en-GB')}</div>
                    </div>
                    <StatusPill status={c.status} />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard;
